import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';

import { MaintenanceService } from '../../core/services/maintenance.service';
import { WaitlistService } from '../../core/services/waitlist.service';
import { IconComponent } from '../../shared/components/icon.component';

export type PrelaunchFormVariant = 'card' | 'bar' | 'editorial';

/**
 * Signup form shown on the gate while the portal is offline.
 *
 * Single opt-in: there is no confirmation email, so the ticked consent box is
 * the whole record of agreement. It is never pre-ticked, because NDPR consent
 * has to be an affirmative act to count for anything.
 */
@Component({
  selector: 'app-prelaunch-form',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ReactiveFormsModule, IconComponent],
  template: `
    @if (waitlist.state() === 'success') {
      <div
        class="w-full text-center"
        [class.py-6]="variant() === 'card' || variant() === 'editorial'"
        role="status"
      >
        <div
          class="size-12 rounded-full bg-forest-500 text-white mx-auto flex items-center justify-center"
        >
          <app-icon name="check-circle" class="w-7 h-7"></app-icon>
        </div>
        <p class="text-forest-800 font-semibold mt-4">You're on the list</p>
        <p class="text-slate-500 text-sm mt-1">
          We'll email you at {{ submittedEmail }} as soon as the registry is live.
        </p>
      </div>
    } @else {
      <form
        id="prelaunch-form"
        [formGroup]="form"
        (ngSubmit)="submit()"
        class="w-full"
        [class.text-left]="variant() !== 'bar'"
        novalidate
      >
        @if (variant() === 'card') {
          <div class="text-center mb-4">
            <p class="inline-flex items-center gap-1.5 text-[11px] font-semibold text-[#1A8F54] mb-1">
              <app-icon name="bell" class="w-3.5 h-3.5"></app-icon>
              Be the first to know!
            </p>
            <h2 class="text-[1.15rem] font-extrabold text-[#163027] leading-tight">
              Join the Pre-Launch List
            </h2>
            <p class="text-slate-500 text-[11px] mt-1 leading-relaxed">
              Subscribe now and get exclusive updates when we launch.
            </p>
          </div>
        }
        @if (variant() === 'editorial') {
          <div class="mb-5">
            <p class="inline-flex items-center gap-1.5 text-[11px] font-semibold text-[#0B5C38]">
              <app-icon name="bell" class="w-3.5 h-3.5"></app-icon>
              Be the first to know!
            </p>
            <h2
              class="mt-2 text-[1.55rem] font-semibold leading-none tracking-tight text-[#1c241d]"
              style="font-family: 'Source Serif 4', Georgia, serif"
            >
              Join the Pre-Launch List
            </h2>
            <p class="mt-2 text-[13px] leading-relaxed text-[#5c645c]">
              Subscribe now and get exclusive updates when we launch.
            </p>
          </div>
        }

        <div [class]="fieldsClass()">
          <div [class]="fieldWrapClass()">
            <label class="sr-only" for="prelaunch-name">Full name</label>
            <div class="relative">
              <app-icon
                name="user"
                class="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none"
              ></app-icon>
              <input
                id="prelaunch-name"
                type="text"
                formControlName="name"
                autocomplete="name"
                placeholder="Full Name"
                [attr.aria-invalid]="isInvalid('name') ? 'true' : null"
                [attr.aria-describedby]="isInvalid('name') ? 'prelaunch-name-error' : null"
                [class]="inputClass()"
              />
            </div>
            @if (isInvalid('name')) {
              <p id="prelaunch-name-error" class="text-red-600 text-xs mt-1">Please tell us your name.</p>
            }
          </div>

          <div [class]="fieldWrapClass()">
            <label class="sr-only" for="prelaunch-email">Email address</label>
            <div class="relative">
              <app-icon
                name="envelope"
                class="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none"
              ></app-icon>
              <input
                id="prelaunch-email"
                type="email"
                formControlName="email"
                autocomplete="email"
                inputmode="email"
                placeholder="Email Address"
                [attr.aria-invalid]="isInvalid('email') ? 'true' : null"
                [attr.aria-describedby]="isInvalid('email') ? 'prelaunch-email-error' : null"
                [class]="inputClass()"
              />
            </div>
            @if (isInvalid('email')) {
              <p id="prelaunch-email-error" class="text-red-600 text-xs mt-1">
                Please enter an email address we can reach you at.
              </p>
            }
          </div>

          <div [class]="fieldWrapClass()">
            <label class="sr-only" for="prelaunch-phone">Phone number</label>
            <div class="relative">
              <app-icon
                name="phone"
                class="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none"
              ></app-icon>
              <input
                id="prelaunch-phone"
                type="tel"
                formControlName="phone"
                autocomplete="tel"
                inputmode="tel"
                placeholder="Phone Number"
                [attr.aria-invalid]="isInvalid('phone') ? 'true' : null"
                [attr.aria-describedby]="isInvalid('phone') ? 'prelaunch-phone-error' : null"
                [class]="inputClass()"
              />
            </div>
            @if (isInvalid('phone')) {
              <p id="prelaunch-phone-error" class="text-red-600 text-xs mt-1">
                Please enter a valid phone number.
              </p>
            }
          </div>

          @if (variant() === 'bar') {
            <button
              type="submit"
              [disabled]="waitlist.state() === 'submitting' || !consentText()"
              class="inline-flex items-center justify-center gap-2 bg-forest-700 hover:bg-forest-800 disabled:opacity-70 text-white rounded-lg font-semibold px-5 py-2.5 text-sm whitespace-nowrap transition-colors"
            >
              @if (waitlist.state() === 'submitting') {
                <app-icon name="arrow-path" class="w-4 h-4 animate-spin"></app-icon>
                Joining…
              } @else {
                Notify Me
              }
            </button>
          }
        </div>

        <div class="hidden" aria-hidden="true">
          <label for="prelaunch-website">Website</label>
          <input
            id="prelaunch-website"
            type="text"
            formControlName="website"
            tabindex="-1"
            autocomplete="off"
          />
        </div>

        @if (consentText()) {
          <div class="flex items-start gap-2 mt-3">
            <input
              id="prelaunch-consent"
              type="checkbox"
              formControlName="consent"
              [attr.aria-invalid]="isInvalid('consent') ? 'true' : null"
              [attr.aria-describedby]="isInvalid('consent') ? 'prelaunch-consent-error' : null"
              class="mt-0.5 h-4 w-4 shrink-0 rounded border-slate-300 text-forest-500 focus:ring-forest-500"
            />
            <label for="prelaunch-consent" class="text-slate-500 text-[11px] leading-snug">
              {{ consentText() }}
            </label>
          </div>
          @if (isInvalid('consent')) {
            <p id="prelaunch-consent-error" class="text-red-600 text-xs mt-1">
              Please tick the box so we know you're happy to hear from us.
            </p>
          }
        }

        @if (variant() !== 'bar') {
          <button
            type="submit"
            [disabled]="waitlist.state() === 'submitting' || !consentText()"
            class="w-full mt-3 inline-flex items-center justify-center gap-2 bg-[#0B5C38] hover:bg-[#09492c] disabled:opacity-70 text-white rounded-lg font-semibold px-6 py-2.5 text-sm transition-colors shadow-[0_8px_18px_rgba(11,92,56,0.22)]"
          >
            @if (waitlist.state() === 'submitting') {
              <app-icon name="arrow-path" class="w-5 h-5 animate-spin"></app-icon>
              Joining…
            } @else {
              Notify Me on Launch
              <app-icon name="paper-airplane" class="w-5 h-5"></app-icon>
            }
          </button>
        }

        <p class="sr-only" role="status" aria-live="polite">{{ statusMessage() }}</p>

        @if (waitlist.state() === 'error') {
          <p class="text-red-600 text-sm mt-3 text-center">{{ waitlist.error() }}</p>
        }

        <p class="flex items-center justify-center gap-1.5 text-slate-400 text-[11px] mt-3">
          <app-icon name="lock-closed" class="w-3.5 h-3.5"></app-icon>
          We respect your privacy. Your information is safe with us.
        </p>
      </form>
    }
  `,
})
export class PrelaunchFormComponent {
  private readonly fb = inject(FormBuilder);
  private readonly maintenance = inject(MaintenanceService);
  readonly waitlist = inject(WaitlistService);

  readonly variant = input<PrelaunchFormVariant>('card');
  readonly consentText = this.maintenance.consentText;

  readonly form = this.fb.nonNullable.group({
    name: ['', [Validators.required, Validators.maxLength(255)]],
    email: ['', [Validators.required, Validators.email, Validators.maxLength(255)]],
    phone: ['', [Validators.required, Validators.maxLength(30), Validators.pattern(/^[+0-9\s().-]{7,30}$/)]],
    consent: [false, [Validators.requiredTrue]],
    website: [''],
  });

  submittedEmail = '';

  readonly fieldsClass = computed(() =>
    this.variant() === 'bar'
      ? 'grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-[1fr_1fr_1fr_auto] gap-3 items-start'
      : 'space-y-2.5'
  );

  readonly fieldWrapClass = computed(() => (this.variant() === 'bar' ? 'min-w-0' : ''));

  readonly inputClass = computed(() =>
    this.variant() === 'editorial'
      ? 'w-full rounded-md border border-[#ddd6c8] bg-[#fbf8f1] text-[#1c241d] placeholder-[#8a918a] pl-10 pr-3 py-2.5 text-sm outline-none focus:border-[#0B5C38] focus:bg-white focus:ring-2 focus:ring-[#0B5C38]/12'
      : 'w-full rounded-lg border border-slate-200 bg-[#FCFDFD] text-slate-800 placeholder-slate-400 pl-10 pr-3 py-2 text-sm outline-none focus:border-[#1A8F54] focus:bg-white focus:ring-2 focus:ring-[#1A8F54]/15'
  );

  readonly statusMessage = computed(() => {
    switch (this.waitlist.state()) {
      case 'submitting':
        return 'Joining the prelaunch list.';
      case 'success':
        return 'You have joined the prelaunch list.';
      case 'error':
        return this.waitlist.error();
      default:
        return '';
    }
  });

  isInvalid(control: keyof typeof this.form.controls): boolean {
    const field = this.form.controls[control];
    return field.invalid && (field.touched || field.dirty);
  }

  submit(): void {
    if (this.waitlist.state() === 'submitting') {
      return;
    }

    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const { name, email, phone, consent } = this.form.getRawValue();
    this.submittedEmail = email;

    this.waitlist
      .join({
        name,
        email,
        phone,
        consent,
        source: 'portal_gate',
      })
      .subscribe({ error: () => undefined });
  }
}
